/**
 * Вкладка «Контент-план» (задачи Оли №376, №383, №390): ОДНА соцсеть за раз (переключатель сетей — в шапке
 * `PostsPage`, `?net=`). Утверждённые сети публикаций без отметки «опубликовано» делятся (`splitPlanned`):
 * «Отложенные» — с датой выкладки, от ранней (порядок даёт сервер), «Не запланированы» — без даты; дальше
 * «Опубликовано» (ручные отметки `variant_publication`, от свежей). Плитка — контент поста: обложка в рамке
 * этой сети, заголовок и текст, дата.
 *
 * «Запланировать» (№396, вместо «Пост вышел» и ссылки «назначить дату») открывает тот же календарь сети, что
 * публикация (`ScheduleDialog`, `POST /api/posts/{id}/schedule` с `date_modify` строки), — плитка переезжает между
 * «Отложенными» и «Не запланированы». Автопубликации нет; ручная отметка выкладки — в карточке результата.
 * Данные — `GET /api/posts/content-plan` (`reels/contentplan.py`).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CalendarClock, ExternalLink } from "lucide-react";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";

import { CroppedImage } from "@/components/posts/CroppedImage";
import { ScheduleDialog } from "@/components/posts/ScheduleDialog";
import { btn, btnPrimary, ErrorLine } from "@/components/trends/ui";
import { ApiFailure } from "@/lib/api";
import { thumbUrl, when } from "@/lib/media";
import type { Aspect } from "@/lib/post";
import { postsKeys, schedulePlan } from "@/lib/postsApi";
import {
  dayLabel,
  isOverdue,
  PLAN_PAGE,
  previewPath,
  snippet,
  splitPlanned,
  whenLabel,
  type ContentPlanNetwork,
  type PlannedItem,
  type PublishedItem,
} from "@/lib/postsTabs";

const tile = "flex flex-col gap-1.5 rounded-[var(--radius-card)] border border-border bg-bg p-1.5";
const grid = "grid grid-cols-[repeat(auto-fill,minmax(170px,1fr))] gap-3";

function Picture({ item, aspect }: { item: PlannedItem | PublishedItem; aspect: Aspect }) {
  const path = previewPath(item);
  if (!path) {
    return (
      <div className="grid aspect-[4/5] w-full place-items-center rounded-[var(--radius-ui)] bg-bg-subtle text-[11px] text-fg-faint">
        нет превью
      </div>
    );
  }
  return (
    <CroppedImage
      src={thumbUrl(path)}
      width={item.width}
      height={item.height}
      crop={item.video_ms === null ? item.crop : null}
      aspect={item.video_ms === null ? aspect : "orig"}
      className="rounded-[var(--radius-ui)]"
    />
  );
}

function Snippet({ item }: { item: PlannedItem | PublishedItem }) {
  const s = snippet(item);
  return <p className="line-clamp-3 min-h-[3lh] text-[12px] leading-snug text-fg-muted">{s || "текст не написан"}</p>;
}

function PlannedTile({ item, network }: { item: PlannedItem; network: ContentPlanNetwork }) {
  const client = useQueryClient();
  const [picking, setPicking] = useState(false);
  const save = useMutation({
    mutationFn: (date: string | null) => schedulePlan(item.plan_id, item.date_modify, network.code, date),
    onSuccess: () => client.invalidateQueries({ queryKey: postsKeys.calendar(network.code) }),
    // и после отказа: STALE несёт новый `date_modify` только в перечитанном плане
    onSettled: () => client.invalidateQueries({ queryKey: postsKeys.contentPlan }),
  });
  const stale = save.error instanceof ApiFailure && save.error.code === "STALE";
  const overdue = isOverdue(item.publish_date);
  return (
    <div className={tile} data-annot="отложенный пост">
      <Link to={`/posts/${item.plan_id}`} className="relative block hover:opacity-90" title="Открыть публикацию">
        <Picture item={item} aspect={(network.aspect ?? "orig") as Aspect} />
        {item.media_count > 1 ? (
          <span className="absolute bottom-1 right-1 rounded-[var(--radius-ui)] bg-black/60 px-1 text-[10px] text-white">
            +{item.media_count - 1}
          </span>
        ) : null}
      </Link>
      <Snippet item={item} />
      {item.publish_date ? (
        <span className={`text-[12px] ${overdue ? "text-err" : "text-accent-text"}`}>
          {whenLabel(item.publish_date)}
          {overdue ? " · просрочено" : ""}
        </span>
      ) : (
        <span className="text-[12px] text-fg-faint">
          дата не назначена{item.date_approve ? ` · утверждено ${when(item.date_approve)}` : ""}
        </span>
      )}
      <button
        type="button"
        className={`${btnPrimary} justify-center`}
        title={`Когда выложить в ${network.name}`}
        disabled={save.isPending}
        onClick={() => {
          save.reset();
          setPicking(true);
        }}
      >
        <CalendarClock size={13} /> Запланировать
      </button>
      {stale ? (
        <span className="text-[12px] text-err">Публикацию изменили в другой вкладке — данные обновлены, повторите.</span>
      ) : (
        <ErrorLine error={save.error} />
      )}
      {/* окно — только открытым: стартовая дата берётся из плитки при каждом открытии */}
      {picking ? (
        <ScheduleDialog
          open
          planId={item.plan_id}
          network={network.code}
          networkName={network.name}
          value={item.publish_date}
          onClose={() => setPicking(false)}
          // окно закрывается сразу: модальное окно накрыло бы строку ошибки под кнопкой (как в `NetworkCard`)
          onSave={(date) => {
            setPicking(false);
            save.mutate(date);
          }}
        />
      ) : null}
    </div>
  );
}

function PublishedTile({ item, network }: { item: PublishedItem; network: ContentPlanNetwork }) {
  const open = item.url ? (
    <a href={item.url} target="_blank" rel="noreferrer" className={`${btn} justify-center`}>
      Открыть <ExternalLink size={12} />
    </a>
  ) : (
    <Link to={item.plan_id !== null ? `/posts/${item.plan_id}` : `/results/${item.variant_id}`} className={`${btn} justify-center`}>
      Открыть
    </Link>
  );
  return (
    <div className={tile} data-annot="опубликованный пост">
      <Picture item={item} aspect={(network.aspect ?? "orig") as Aspect} />
      <Snippet item={item} />
      <span className="text-[12px] text-ok">{item.date ? dayLabel(item.date) : "дата не указана"}</span>
      {open}
    </div>
  );
}

function Section({ title, items, empty }: { title: string; items: ReactNode[]; empty: string }) {
  const [shown, setShown] = useState(PLAN_PAGE);
  return (
    <section className="grid gap-3">
      <h2 className="text-[15px] font-medium text-fg">
        {title} <span className="text-[12px] font-normal text-fg-faint">{items.length}</span>
      </h2>
      {items.length ? <div className={grid}>{items.slice(0, shown)}</div> : <span className="text-[12px] text-fg-faint">{empty}</span>}
      {items.length > shown ? (
        <button
          type="button"
          className="w-fit text-[12.5px] text-fg-muted underline underline-offset-2 hover:text-fg"
          onClick={() => setShown((n) => n + PLAN_PAGE)}
        >
          Показать ещё
        </button>
      ) : null}
    </section>
  );
}

export function ContentPlanTab({ network }: { network: ContentPlanNetwork }) {
  const { scheduled, unscheduled } = splitPlanned(network.planned);
  return (
    // ключ — сеть: при смене сети «Показать ещё» начинается заново
    <div key={network.code} className="grid gap-8" data-annot="контент-план сети">
      <Section
        title="Отложенные"
        empty={`Запланированных к выкладке в ${network.name} нет.`}
        items={scheduled.map((it) => <PlannedTile key={it.plan_id} item={it} network={network} />)}
      />
      <Section
        title="Не запланированы"
        empty="Все утверждённые посты с датой."
        items={unscheduled.map((it) => <PlannedTile key={it.plan_id} item={it} network={network} />)}
      />
      <Section
        title="Опубликовано"
        empty="Отметок о выкладке нет."
        items={network.published.map((it) => <PublishedTile key={it.id} item={it} network={network} />)}
      />
    </div>
  );
}
